'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const optionalLink = (host: string) =>
  z
    .string()
    .trim()
    .refine(
      (v) => {
        if (!v) return true;
        try {
          const url = new URL(v);
          return url.protocol === 'https:' && url.host === host;
        } catch {
          return false;
        }
      },
      { message: `Paste an https://${host} link` }
    );

const schema = z.object({
  title: z.string().trim().min(3, 'Give the group a short name').max(120),
  description: z.string().trim().max(2000),
  linearUrl: optionalLink('linear.app'),
  fixPrUrl: optionalLink('github.com'),
});
export type GroupFormValues = z.infer<typeof schema>;

/**
 * The group's own words. A new group asks for its name and cause only;
 * editing also takes the Linear issue and the fix PR.
 */
export function GroupDialog({
  open,
  onOpenChange,
  mode,
  initial,
  replies,
  onSubmit,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initial?: Partial<GroupFormValues>;
  /** How many replies a new group starts with. */
  replies?: number;
  onSubmit: (values: GroupFormValues) => void;
  isSaving: boolean;
}) {
  const form = useForm<GroupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      linearUrl: '',
      fixPrUrl: '',
      ...initial,
    },
  });
  useEffect(() => {
    if (open)
      form.reset({
        title: '',
        description: '',
        linearUrl: '',
        fixPrUrl: '',
        ...initial,
      });
  }, [open, initial, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' ? 'New failure group' : 'Edit failure group'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? `Name the cause the ${replies === 1 ? 'reply has' : `${replies ?? 0} replies have`} in common, so it can be fixed once.`
                : 'The group’s name, cause and where its fix lives.'}{' '}
              Don’t paste what the customer wrote: it stays in Langfuse.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="my-4">
            <Controller
              name="title"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Name</FieldLabel>
                  <Input
                    {...field}
                    id={field.name}
                    placeholder="Promises a callback, never hands off"
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.error && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Cause</FieldLabel>
                  <Textarea
                    {...field}
                    id={field.name}
                    rows={3}
                    placeholder="What Claire does wrong and why, in a sentence or two."
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.error && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            {mode === 'edit' ? (
              <>
                <Controller
                  name="linearUrl"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name}>Linear issue</FieldLabel>
                      <Input
                        {...field}
                        id={field.name}
                        placeholder="https://linear.app/borradh/issue/ENG-…"
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.error && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
                <Controller
                  name="fixPrUrl"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name}>Fix PR</FieldLabel>
                      <Input
                        {...field}
                        id={field.name}
                        placeholder="https://github.com/Borradh-Media/borradh-workspace/pull/…"
                        aria-invalid={fieldState.invalid}
                      />
                      <FieldDescription>
                        The pull request that fixes this cause, once there is
                        one.
                      </FieldDescription>
                      {fieldState.error && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              </>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving
                ? 'Saving…'
                : mode === 'create'
                  ? 'Create group'
                  : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
